import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, Star, Heart, Shield, Truck, Sparkles, CheckCircle2, Lock, Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PayPalButton } from '../components/PayPalButton';
import { StripeButton } from '../components/StripeButton';
import { PaymentTrustBlock } from '../components/PaymentTrustBlock';
import { BundleUpsell } from '../components/BundleUpsell';
import { openBustProductData as productData, bundleData } from '../data/products';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd, breadcrumbSchema } from '../hooks/useJsonLd';

export const OpenBustFajaPage = () => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(productData.colors[0]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity] = useState(1);

  const currentImages = productData.imagesByColor[selectedColor] || [];

  // Hero video — autoplay loop (Carla clip, 7s)
  const heroVideoUrl = "https://customer-assets-m6fa6gv7.emergentagent.net/job_item-landing/artifacts/u3fe5wy9_Carla%20gif1.mp4";
  const heroVideoPoster = currentImages[0];

  useSEO({
    title: 'Open-Bust Tummy Control Faja Colombiana | CurveeForYou',
    description: 'Open-bust Faja Colombiana — wear with any bra. Colombian compression, adjustable straps, hook-and-eye closure, lace trim. Included in the $89.99 Snatched Duo Bundle + free US shipping.',
    canonical: 'https://curveeforyou.com/open-bust-faja'
  });

  // Informational page — the Open-Bust Faja is only sold as part of the $89.99 Snatched Duo Bundle.
  // Product schema lives on the homepage (BundlePage) to avoid conflicting price data in Google Shopping.
  useJsonLd([
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Open-Bust Faja', url: '/open-bust-faja' }
    ])
  ]);

  const handleBuyNow = () => {
    // Bundle-only store — every Buy Now redirects to the bundle homepage
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white pb-20 md:pb-0">

      {/* Top Banner — matches Bundle landing */}
      <div className="bg-gradient-to-r from-[#d4af37] via-[#c49d2a] to-[#d4af37] text-[#5d1729] py-3 px-4">
        <div className="container mx-auto flex items-center justify-center gap-2 text-center">
          <Gift className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm md:text-lg font-bold uppercase tracking-wide">For A Limited Time — Snatched From Bust To Thigh: Buy 2 Fajas, Get FREE Boyshorts</span>
          <Gift className="w-5 h-5 flex-shrink-0" />
        </div>
      </div>

      {/* Sticky Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 max-w-7xl">
          <div className="flex items-center justify-between">
            <Link to="/" aria-label="CurveeForYou home" data-testid="open-bust-nav-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
                alt="CurveeForYou"
                className="h-20 md:h-24 w-auto"
              />
            </Link>

            {/* Desktop Menu — mirrors Bundle landing (lean, conversion-focused) */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8 ml-8 lg:ml-12">
              <a href="#reviews" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors">
                Reviews
              </a>
              <a href="#guarantee" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors">
                Guarantee
              </a>
              <Link to="/about" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors" data-testid="open-bust-nav-about-link">
                About
              </Link>
              <Button
                onClick={handleBuyNow}
                data-testid="open-bust-nav-add-to-cart"
                className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-6"
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Buy Now
              </Button>
            </div>

            {/* Mobile Buy Now Button — matches Full Body Faja */}
            <Button
              onClick={handleBuyNow}
              data-testid="open-bust-mobile-nav-buy-now"
              className="md:hidden bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-4 py-2 text-sm font-bold shadow-lg"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              Buy Now
            </Button>
          </div>
        </div>
      </header>

      {/* Main Product */}
      <section className="container mx-auto px-6 py-10 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-10">

          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="md:hidden flex justify-center mb-2">
              <Badge className="bg-red-600 text-white font-bold text-base px-5 py-2 shadow-lg animate-pulse">
                INCLUDED IN THE $89.99 BUNDLE 🔥
              </Badge>
            </div>

            <div className="relative bg-rose-50/30 rounded-2xl overflow-hidden shadow-2xl aspect-[4/5]">
              {selectedImage === 0 ? (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={heroVideoPoster}
                  className="w-full h-full object-cover"
                  data-testid="open-bust-hero-video"
                  aria-label="Open-Bust Tummy Control Faja product video"
                >
                  <source src={heroVideoUrl} type="video/mp4" />
                  <img
                    src={heroVideoPoster}
                    alt={`Open-bust tummy control Faja Colombiana in ${selectedColor.toLowerCase()} — front hook-and-eye closure, adjustable straps and lace trim`}
                  />
                </video>
              ) : (
                <img
                  src={currentImages[selectedImage]}
                  alt={`Open-bust tummy control Faja Colombiana in ${selectedColor.toLowerCase()} — front hook-and-eye closure, adjustable straps and lace trim for a smooth, sculpted silhouette`}
                  className="w-full h-full object-contain"
                />
              )}
              <Badge className="hidden md:inline-flex absolute top-6 left-6 bg-red-600 text-white font-bold text-base px-5 py-2 shadow-lg animate-pulse">
                INCLUDED IN THE $89.99 BUNDLE 🔥
              </Badge>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {/* Video thumbnail at index 0 */}
              <button
                onClick={() => setSelectedImage(0)}
                data-testid="open-bust-thumbnail-video"
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  selectedImage === 0
                    ? 'border-[#7d1f3d] shadow-lg scale-105'
                    : 'border-gray-200 hover:border-[#7d1f3d]/50'
                }`}
              >
                <img
                  src={heroVideoPoster}
                  alt="Product video thumbnail"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="bg-white/90 rounded-full p-2">
                    <svg className="w-4 h-4 text-[#7d1f3d]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Remaining image thumbnails (skip index 0 since it's the video poster) */}
              {currentImages.slice(1).map((image, idx) => {
                const imgIdx = idx + 1;
                return (
                  <button
                    key={image}
                    onClick={() => setSelectedImage(imgIdx)}
                    data-testid={`open-bust-thumbnail-${imgIdx}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === imgIdx
                        ? 'border-[#7d1f3d] shadow-lg scale-105'
                        : 'border-gray-200 hover:border-[#7d1f3d]/50'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${productData.name} - ${selectedColor} colorway - view ${imgIdx + 1}`}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6 lg:sticky lg:top-32 self-start">
            <div>
              <Badge className="bg-rose-100 text-[#7d1f3d] mb-3 px-3 py-1 font-semibold">
                WEDDING · PROM · STRAPLESS
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-3 leading-tight">
                {productData.name}
              </h1>
              <p className="text-xl text-[#d4af37] italic font-semibold mb-4">
                {productData.tagline}
              </p>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />
                  ))}
                </div>
                <span className="text-gray-600 text-sm">
                  {productData.rating}/5 ({productData.reviewCount.toLocaleString()} reviews)
                </span>
              </div>

              <div className="text-gray-700 space-y-3 mt-4 whitespace-pre-line">
                {productData.description}
              </div>
            </div>

            {/* Bundle-only CTA (replaces size selectors + individual checkout) */}
            <div id="payment-section" className="pt-2">
              <Link
                to="/"
                data-testid="open-bust-checkout-bundle-cta"
                className="block bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-shadow"
              >
                <p className="text-[#d4af37] text-xs font-bold uppercase tracking-wider mb-2">Bundle Exclusive</p>
                <p className="text-white font-bold text-xl mb-1">Only available in the Snatched Duo Bundle</p>
                <p className="text-white/80 text-sm mb-4">Get this Open-Bust Faja together with the Full Body Faja + FREE Seamless Boyshorts.</p>
                <div className="bg-[#d4af37] text-[#5d1729] text-center font-bold py-3 rounded-lg">
                  Get the Bundle — $89.99 →
                </div>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
              <div className="text-center">
                <Truck className="w-6 h-6 text-[#7d1f3d] mx-auto mb-1" />
                <p className="text-xs font-semibold">Free US Shipping</p>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 text-[#7d1f3d] mx-auto mb-1" />
                <p className="text-xs font-semibold">30-Day Guarantee</p>
              </div>
              <div className="text-center">
                <Heart className="w-6 h-6 text-[#7d1f3d] mx-auto mb-1" />
                <p className="text-xs font-semibold">1,247+ Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-gradient-to-b from-white to-rose-50/50 py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#7d1f3d] text-center mb-12">Why It&apos;s Already In Your Wardrobe&apos;s Future</h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
              {productData.features.map((f) => (
                <div key={f.title} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#7d1f3d] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900">{f.title}</p>
                    <p className="text-gray-600 text-sm">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Cross-Sell */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-2xl p-10 text-center">
            <Badge className="bg-[#d4af37] text-[#5d1729] font-bold mb-4">FREQUENTLY BOUGHT TOGETHER</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Get Snatched Bust To Thigh</h3>
            <p className="text-xl text-rose-100 mb-2">
              Buy any 2 fajas, get our high-quality seamless boyshorts <span className="text-[#d4af37] italic font-bold">free</span>.
            </p>
            <div className="my-6">
              <span className="text-5xl font-bold text-[#d4af37]">${bundleData.price.toFixed(2)}</span>
              <p className="text-sm text-rose-100 mt-2">+ FREE High-Quality Seamless Boyshorts</p>
            </div>
            <Link to="/bundle">
              <Button className="bg-[#d4af37] hover:bg-[#c49d2a] text-[#5d1729] font-bold px-8 py-6 text-lg" data-testid="open-bust-bundle-cta">
                Get the Bundle + Free Gift
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile sticky bar */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <Link
          to="/"
          data-testid="open-bust-mobile-sticky-buy-now"
          className="block w-full bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold text-base text-center py-4 rounded-lg shadow-lg"
        >
          Get the Bundle — $89.99 →
        </Link>
      </div>
    </div>
  );
};
